import type { EdgeBar } from '../types/chart'
import { dailyRowsFromBars, sessionDateOf, type SessionRow } from '../utils/mtfSession'
import type { MultiPeriodCache } from './MultiPeriodCache'

/**
 * The daily series behind a chart's 1D/1W indicators (MTF Phase B): one per
 * ChartManager, reading through the datafeed's SHARED multi-period cache.
 *
 * A live chart's series IS that cache's `1D` entry for (ticker, adjusted): the
 * same request the background preload makes and our own 1D chart's first page
 * asks for (all three use `fetchPeriodWindow`'s window), so a chart showing a
 * 200-day line, its 1D view and every grid cell on the ticker share one fetch.
 * Unlike the preload this is not skipped for grid cells or hidden charts: an
 * indicator the user configured needs its data wherever it is drawn.
 *
 * An anchored chart (History, replay) needs the five years BEFORE its date, not
 * before today, so it fetches its own window ending at the anchor. That one is
 * per chart and cancelled on change or dispose.
 *
 * Refetches when the session date rolls over (yesterday's official bar then
 * replaces the one the chart built from its own bars), when the shared entry
 * goes stale, and when it is evicted mid-flight (the abort is retried rather
 * than leaving the line empty).
 */

export interface HtfSeriesDeps {
  /** The shared per-datafeed cache (`getSharedMultiPeriodCache`). */
  cache: MultiPeriodCache
  ticker(): string
  adjusted(): boolean
  /** End of the anchored window, or null for a live chart. */
  windowEnd(): number | null
  /** Fetch 1D bars ending at `endMs` (null: now) through the chart's window maths. */
  fetch(endMs: number | null, signal: AbortSignal): Promise<EdgeBar[]>
  /** A series landed: recompute whatever reads it. */
  onReady(): void
  now(): number
}

interface Source {
  key: string
  ticker: string
  adjusted: boolean
  promise: Promise<EdgeBar[]>
  rows: SessionRow[] | null
}

/** Evicted mid-flight this many times in a row: back off instead of thrashing. */
const MAX_ABORT_RETRIES = 3
/** Wait after a failed fetch before the next trigger may retry. */
const RETRY_AFTER_MS = 30_000

function isAbortError(err: unknown): boolean {
  return (err as { name?: unknown } | null)?.name === 'AbortError'
}

export class HtfSeriesTracker {
  private readonly deps: HtfSeriesDeps
  /** The source for the chart's current ticker / mode / window. */
  private current: Source | null = null
  /** The last source that resolved: served while a refetch is in flight. */
  private settled: Source | null = null
  private anchoredController: AbortController | null = null
  private failed: Promise<EdgeBar[]> | null = null
  private abortRetries = 0
  private retryAt = 0
  private disposed = false

  constructor(deps: HtfSeriesDeps) {
    this.deps = deps
  }

  /** Make sure the series for the chart's current state is loaded or loading.
   *  Idempotent: cheap to call on every trigger (load, tick, toggle). */
  ensure(): void {
    if (this.disposed) return
    const now = this.deps.now()
    if (now < this.retryAt) return
    const ticker = this.deps.ticker()
    const adjusted = this.deps.adjusted()
    const end = this.deps.windowEnd()
    if (end == null) this.ensureLive(ticker, adjusted, now)
    else this.ensureAnchored(ticker, adjusted, end)
  }

  /** Daily rows for the chart's ticker and mode, or undefined while none have
   *  landed. A refetch keeps serving the previous rows of the SAME ticker and
   *  mode; another ticker's rows are never served. */
  rows(): readonly SessionRow[] | undefined {
    const ticker = this.deps.ticker()
    const adjusted = this.deps.adjusted()
    for (const source of [this.current, this.settled]) {
      if (source?.rows && source.ticker === ticker && source.adjusted === adjusted) {
        return source.rows
      }
    }
    return undefined
  }

  dispose(): void {
    this.disposed = true
    this.anchoredController?.abort()
    this.anchoredController = null
  }

  private ensureLive(ticker: string, adjusted: boolean, now: number): void {
    this.anchoredController?.abort()
    this.anchoredController = null
    const opts = { adjusted }
    const hit = this.deps.cache.get(ticker, '1D', opts)
    const fresh =
      hit != null &&
      hit.promise !== this.failed &&
      !this.deps.cache.isStale(ticker, '1D', opts) &&
      sessionDateOf(hit.fetchedAt) === sessionDateOf(now)
    const key = `${ticker}|${adjusted ? 1 : 0}|live`
    if (fresh) {
      if (this.current?.promise !== hit.promise) this.track(key, ticker, adjusted, hit.promise)
      return
    }
    const controller = new AbortController()
    const promise = this.deps.fetch(null, controller.signal)
    promise.catch(() => undefined)
    this.deps.cache.set(ticker, '1D', promise, controller, opts)
    this.track(key, ticker, adjusted, promise)
  }

  private ensureAnchored(ticker: string, adjusted: boolean, end: number): void {
    // Keyed by the window's last day, the alignment the datafeed applies.
    const key = `${ticker}|${adjusted ? 1 : 0}|${Math.ceil(end / 86_400_000)}`
    if (this.current?.key === key) return
    this.anchoredController?.abort()
    const controller = new AbortController()
    this.anchoredController = controller
    const promise = this.deps.fetch(end, controller.signal)
    promise.catch(() => undefined)
    this.track(key, ticker, adjusted, promise)
  }

  private track(key: string, ticker: string, adjusted: boolean, promise: Promise<EdgeBar[]>) {
    const source: Source = { key, ticker, adjusted, promise, rows: null }
    this.current = source
    promise.then(
      (bars) => {
        if (this.disposed || this.current !== source) return
        source.rows = dailyRowsFromBars(bars)
        this.settled = source
        this.abortRetries = 0
        this.deps.onReady()
      },
      (err: unknown) => {
        if (this.disposed || this.current !== source) return
        this.current = null
        this.failed = promise
        if (isAbortError(err) && this.abortRetries < MAX_ABORT_RETRIES) {
          // Evicted from the shared cache while in flight: fetch again.
          this.abortRetries++
          this.ensure()
        } else {
          this.retryAt = this.deps.now() + RETRY_AFTER_MS
        }
      },
    )
  }
}
